import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DropdownItem, ModalRef, ModalService } from '@vinnova/ui';
import { BehaviorSubject, filter, map, Observable, of, take } from 'rxjs';
import { ReadonlyState } from '../../../../core/models/form.model';
import {
  Classification,
  ClassificationChoosenAnswer,
  ClassificationTypeAnswer,
  ClassificationTypes,
  DescriptionItems,
} from '../../../../core/models/question/question-classification.model';
import { BaseClassificationService } from '../../../../core/services/base-classification.service';
import { FormValidationService } from '../../../../core/services/form-validation.service';

@UntilDestroy()
@Component({
  selector: 'lib-classification-type',
  templateUrl: './classification-type.component.html',
  styleUrls: ['./classification-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ClassificationTypeComponent implements OnInit, OnChanges {
  @Input() label: string;
  @Input() typeId: string;
  @Input() parsedClassificationAnswer: ClassificationTypeAnswer;
  @Input() readOnlyState: ReadonlyState;
  @Input() questionId: string;
  @Input() showChangeIndicatorForIds: string[] = [];
  @Output() classificationAnswer = new EventEmitter();

  allClassificationsSubject = new BehaviorSubject<Classification[]>([]);
  allClassifications$ = this.allClassificationsSubject.asObservable();
  avdelningItems = new BehaviorSubject<DropdownItem[]>([]);
  avdelningItems$ = this.avdelningItems.asObservable();
  huvudGruppItems = new BehaviorSubject<DropdownItem[]>([]);
  huvudGruppItems$ = this.huvudGruppItems.asObservable();
  gruppItems = new BehaviorSubject<DropdownItem[]>([]);
  gruppItems$ = this.gruppItems.asObservable();
  classificationItems = new BehaviorSubject<DropdownItem[]>([]);
  classificationItems$ = this.classificationItems.asObservable();
  activeLang$: Observable<string>;
  addButtonText: string;

  ClassificationTypes = ClassificationTypes;
  oldLanguage: string;
  classificationParentId: string;
  classificationModal: ModalRef;
  maxSelectedClassifications: boolean;
  errorMessage$: Observable<string>;

  selectedAvdelning: string;
  selectedHuvudgrupp: string;
  selectedGrupp: string;
  selectedKlassificering: string;
  selectedSearchValue: string;
  editItemIdentifier: string;
  editItem: Classification;

  choosenItem: Classification | DropdownItem;
  choosenItems = [];
  choosenItemsWorkingCopy = [];
  searchValues = [];
  searchValuesCopy = [];
  allClassifications: Classification[] = [];
  descriptionItems: DescriptionItems[] = [];
  firstLevelList: Classification[] = [];

  allowSetClassificationItems: boolean;
  linkButtonIsCollapsed = true;

  ReadonlyState = ReadonlyState;

  constructor(
    private modalService: ModalService,
    private classificationService: BaseClassificationService,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    private formValidationService: FormValidationService,
  ) {}

  ngOnInit(): void {
    this.activeLang$ = this.translocoService.langChanges$;
    this.oldLanguage = this.translocoService.getActiveLang();

    this.classificationService
      .getAllClassification(this.typeId)
      .pipe(untilDestroyed(this))
      .subscribe((val) => this.allClassificationsSubject.next(val));

    this.formValidationService.showErrors$
      .pipe(
        filter((err) => err),
        untilDestroyed(this),
      )
      .subscribe(() => this.errorCheck());

    switch (this.typeId) {
      case ClassificationTypes.BO: {
        this.addButtonText = 'classification_add_BO';
        break;
      }
      case ClassificationTypes.FO: {
        this.addButtonText = 'classification_add_FO';
        break;
      }
      case ClassificationTypes.PO: {
        this.addButtonText = 'classification_add_PO';
        break;
      }
      // No default
    }
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parsedClassificationAnswer']) {
      this.allClassifications$
        .pipe(
          filter((val) => val && val.length > 0),
          take(1),
        )
        .subscribe((val) => {
          this.choosenItems = [];
          this.searchValues = [];
          this.allClassifications = val;
          this.setSearchValues();
          if (this.typeId === ClassificationTypes.BO) {
            this.allClassifications.sort(this.objectComparisonCallback);
            //Här ska det vara null och inte undefined
            // eslint-disable-next-line unicorn/no-null
            this.classificationItems$ = this.getClassificationsForParent(null);
            this.setDescriptionItems();
            if (this.parsedClassificationAnswer?.choosenItems.length > 0) {
              for (const c of this.searchValues) {
                this.setChoosenItems(
                  this.parsedClassificationAnswer.choosenItems,
                  c,
                );
              }
              this.cdr.detectChanges();
            } else {
              this.setParsedClassificationAnswer();
              this.cdr.detectChanges();
            }
          } else {
            //Här ska det vara null och inte undefined
            // eslint-disable-next-line unicorn/no-null
            this.avdelningItems$ = this.getClassificationsForParent(null);
            for (const c of this.allClassifications) {
              if (c.level === 1) {
                this.firstLevelList.push(c);
              }
            }
            if (this.parsedClassificationAnswer?.choosenItems.length > 0) {
              for (const c of this.searchValues) {
                this.setChoosenItems(
                  this.parsedClassificationAnswer.choosenItems,
                  c,
                );
              }
              this.choosenItems.sort((a, b) => (a.prio < b.prio ? -1 : 1));
              this.cdr.detectChanges();
            } else {
              this.setParsedClassificationAnswer();
              this.cdr.detectChanges();
            }

            if (this.typeId === ClassificationTypes.FO) {
              this.firstLevelList.sort(this.objectComparisonCallback);
            } else {
              this.firstLevelList.sort(this.objectComparisonCallbackString);
            }

            this.setDescriptionItems();
          }

          if (this.typeId === ClassificationTypes.BO) {
            this.checkMaxSelectedClassifications(1);
          } else {
            this.checkMaxSelectedClassifications(3);
          }
        });
    }
    if (changes['readOnlyState'] && !changes['readOnlyState'].firstChange) {
      const takingOverEditing =
        changes['readOnlyState'].previousValue === ReadonlyState.Reader &&
        this.readOnlyState === ReadonlyState.Editable;
      if (!takingOverEditing) {
        this.errorCheck();
      }
    }
  }

  // Confirm choice
  confirmClassification(): void {
    if (
      (this.choosenItemsWorkingCopy as DropdownItem[]).some(
        (x) => x.value === this.selectedKlassificering,
      ) ||
      !this.selectedKlassificering
    ) {
      this.selectedKlassificering = undefined;
      return;
    }
    const oldChoosenItem = this.choosenItem;
    for (const val of this.searchValues) {
      if (val.value === this.selectedKlassificering) {
        this.choosenItem = val;
        break;
      }
    }
    if (this.typeId === ClassificationTypes.BO) {
      this.choosenItemsWorkingCopy[0] = this.choosenItem;
      if (this.editItemIdentifier) {
        this.editItemIdentifier = (this.choosenItem as DropdownItem).value;
      }
      this.checkMaxSelectedClassifications(1);
    } else {
      const c = this.getClassification(this.editItemIdentifier);
      if (
        this.choosenItemsWorkingCopy.length < 3 ||
        this.editItemIdentifier !== undefined
      ) {
        if (this.editItemIdentifier === undefined) {
          this.choosenItemsWorkingCopy.push(this.choosenItem);
        } else {
          for (const index in this.choosenItemsWorkingCopy) {
            if (
              this.choosenItemsWorkingCopy[index].value ===
              this.editItemIdentifier
            ) {
              this.choosenItemsWorkingCopy[index] = this.choosenItem;
            }
          }
          this.pushSearchValue(c);
          this.searchValues.sort(this.searchValueComparisonCallback);
          this.editItemIdentifier = (this.choosenItem as DropdownItem).value;
        }

        this.updateDropdownsIfDifferentParent(
          this.editItemIdentifier === undefined
            ? (oldChoosenItem as Classification)?.parentId
            : c.parentId,
        );
        this.searchValues = this.searchValues.filter(
          (val) => val.value !== this.selectedKlassificering,
        );
        this.checkMaxSelectedClassifications(3);
      }
    }

    if (this.classificationParentId || this.typeId === ClassificationTypes.BO) {
      this.classificationItems$ = this.getFinalClassificationsForParent(
        this.classificationParentId,
      );
    }
    this.setPrioForChoosenItems(this.choosenItemsWorkingCopy);
    setTimeout(() => {
      this.selectedSearchValue = undefined;
      this.classificationItems$ = this.getFinalClassificationsForParent(
        (this.choosenItem as Classification).parentId,
      );
      this.selectedKlassificering = undefined;
    });
  }

  private updateDropdownsIfDifferentParent(parentId: string) {
    if (parentId !== (this.choosenItem as Classification).parentId)
      this.setClassificationEditValues(
        (this.choosenItem as DropdownItem).value,
      );
  }

  onConfirmModal() {
    this.choosenItems = this.choosenItemsWorkingCopy;
    this.setPrioForChoosenItems(this.choosenItems);
    this.setAnswerForType();
    this.closeModal();
  }

  closeModal() {
    this.formValidationService.triggerSectionInvalidCheck(this.questionId);
    this.classificationModal.hide();
  }

  afterCloseModal() {
    this.editItemIdentifier = undefined;
    this.selectedAvdelning = undefined;
    this.selectedHuvudgrupp = undefined;
    this.selectedGrupp = undefined;
    this.selectedKlassificering = undefined;
    this.selectedSearchValue = undefined;
    this.editItem = undefined;
    this.classificationAnswer.emit(this.parsedClassificationAnswer);
    this.errorCheck();
    if (this.typeId === this.ClassificationTypes.BO) {
      //Här ska det vara null och inte undefined eftersom
      // vissa classificationItems har null som parentId
      // eslint-disable-next-line unicorn/no-null
      this.classificationItems$ = this.getClassificationsForParent(null);
    } else {
      this.huvudGruppItems$ = this.getClassificationsForParent(undefined);
      this.classificationItems$ = this.getClassificationsForParent(undefined);
      this.gruppItems$ = this.getClassificationsForParent(undefined);
    }
    this.classificationModal = undefined;
  }

  private getClassification(id: string): Classification {
    return this.allClassifications.find((c) => c.identifier === id);
  }

  private setClassificationEditValues(id: string) {
    const currentClassification = this.getClassification(id);
    if (this.editItemIdentifier && !this.editItem) {
      this.editItem = currentClassification;
    }
    this.selectedKlassificering =
      this.editItemIdentifier ?? (this.choosenItem as DropdownItem).value;
    this.classificationItems$ = this.getFinalClassificationsForParent(
      currentClassification.parentId,
    );

    if (this.typeId === ClassificationTypes.BO) return;

    const gruppParent = this.getClassification(currentClassification.parentId);
    if (this.typeId === ClassificationTypes.PO) {
      this.gruppItems$ = this.getClassificationsForParent(gruppParent.parentId);
      this.selectedGrupp = gruppParent.identifier;
    }
    const huvudgruppParent = this.getClassification(
      this.typeId === ClassificationTypes.PO
        ? gruppParent.parentId
        : currentClassification.parentId,
    );
    this.huvudGruppItems$ = this.getClassificationsForParent(
      huvudgruppParent.parentId,
    );
    this.selectedHuvudgrupp = huvudgruppParent.identifier;

    const avdelningParent = this.getClassification(huvudgruppParent.parentId);
    this.avdelningItems$ = this.getClassificationsForParent(
      avdelningParent.parentId,
    );
    this.selectedAvdelning = avdelningParent.identifier;
    this.selectedHuvudgrupp = huvudgruppParent.identifier;
  }

  addClassificationLibModal(content: TemplateRef<unknown>, id?: string) {
    if (id) {
      this.editItemIdentifier = id;
      this.setClassificationEditValues(id);
    }
    this.choosenItemsWorkingCopy = this.choosenItems.filter(Boolean);
    if (this.typeId !== ClassificationTypes.BO) {
      this.searchValues = this.searchValuesCopy.filter((val) => {
        let classificationIsChoosen = false;
        for (const choosen of this.choosenItemsWorkingCopy) {
          if (choosen.value === val.value) {
            classificationIsChoosen = true;
          }
        }
        return !classificationIsChoosen;
      });
    }
    this.classificationModal = this.modalService.show(content, {
      backdrop: 'static',
      class: 'modal-md',
    });
    this.modalService
      .onHide(this.classificationModal)
      .pipe(take(1))
      .subscribe(() => this.afterCloseModal());
    if (this.oldLanguage !== this.translocoService.getActiveLang()) {
      for (const searchValue of this.searchValues) {
        searchValue.label =
          this.translocoService.getActiveLang() === 'sv'
            ? searchValue.name
            : searchValue.nameEng;
      }
      this.oldLanguage = this.translocoService.getActiveLang();
    }
    if (this.typeId === this.ClassificationTypes.BO) {
      this.checkMaxSelectedClassifications(1);
    } else {
      this.checkMaxSelectedClassifications(3);
    }
  }

  // Answer
  setChoosenItems(
    classificationTypeAnswerList: ClassificationChoosenAnswer[],
    c,
  ) {
    for (const ans of classificationTypeAnswerList) {
      if (ans.identifier === c.value) {
        this.choosenItems.push({
          value: c.value,
          label:
            this.translocoService.getActiveLang() === 'sv' ? c.name : c.nameEng,
          name: c.name,
          nameEng: c.nameEng,
          prio: ans.order,
        });
      }
    }
  }

  setParsedClassificationAnswer() {
    this.parsedClassificationAnswer = {
      typeId: '',
      choosenItems: [],
    };
    this.choosenItems = [];
  }

  setAnswerForType() {
    this.parsedClassificationAnswer.typeId = this.typeId;
    this.parsedClassificationAnswer.choosenItems = this.setAnswer();
  }

  setAnswer() {
    const answers: ClassificationChoosenAnswer[] = [];
    for (const [i, choosen] of this.choosenItems.entries()) {
      answers.push({
        identifier: choosen.value,
        order: i + 1,
        nameSv: choosen.name,
        nameEn: choosen.nameEng,
      });
    }
    return answers;
  }

  // Get values for dropdown lists depending on parent dropdown choice
  modelAvdelningChange($event): void {
    this.huvudGruppItems$ = this.getClassificationsForParent($event);

    this.classificationItems$ = this.getClassificationsForParent(undefined);
    this.gruppItems$ = this.getClassificationsForParent(undefined);

    this.selectedHuvudgrupp = undefined;
    this.selectedGrupp = undefined;
    this.selectedKlassificering = undefined;
  }

  modelHuvudGruppChange($event): void {
    this.classificationParentId = $event;
    if (this.typeId === ClassificationTypes.FO) {
      this.allowSetClassificationItems = true;
      this.classificationItems$ = this.getFinalClassificationsForParent($event);
    } else {
      this.gruppItems$ = this.getClassificationsForParent($event);
      this.classificationItems$ = this.getClassificationsForParent(undefined);
    }

    this.selectedGrupp = undefined;
    this.selectedKlassificering = undefined;
  }

  modelGruppChange($event): void {
    this.classificationParentId = $event;
    this.allowSetClassificationItems = true;
    this.classificationItems$ = this.getFinalClassificationsForParent($event);

    this.selectedKlassificering = undefined;
  }

  private getClassificationsForParent(
    parentId: string,
  ): Observable<DropdownItem[]> {
    return this.allClassifications$.pipe(
      map((classifications) => {
        const filtered =
          parentId === null
            ? classifications.filter((cl) => cl.parentId === null)
            : classifications.filter((cl) => cl.parentId === parentId);
        if (this.typeId === ClassificationTypes.PO && !parentId) {
          filtered.sort(this.objectComparisonCallbackString);
        } else {
          filtered.sort(this.objectComparisonCallback);
        }
        return this.returnFilteredAsDropDownList(filtered);
      }),
    );
  }

  private getFinalClassificationsForParent(
    parentId: string,
  ): Observable<DropdownItem[]> {
    return this.allClassifications$.pipe(
      map((classifications) => {
        const filtered = classifications.filter((cl) => {
          let classificationIsChoosen = false;
          if (
            (this.choosenItemsWorkingCopy as DropdownItem[]).some(
              (x) => x.value === cl.identifier,
            )
          ) {
            for (const choosen of this.choosenItemsWorkingCopy) {
              if (
                choosen.value === cl.identifier &&
                choosen.value !== this.selectedKlassificering
              ) {
                classificationIsChoosen = true;
              }
            }
          }
          return (
            (cl.parentId === parentId && !classificationIsChoosen) ||
            (undefined === parentId && !classificationIsChoosen)
          );
        });
        filtered.sort(this.objectComparisonCallback);
        return this.returnFilteredAsDropDownList(filtered);
      }),
    );
  }

  // SearchValues
  onSelectSearchValue() {
    this.selectedKlassificering = this.selectedSearchValue;
    this.confirmClassification();
  }

  setSearchValues(): void {
    const maxLevel = Math.max(...this.allClassifications.map((c) => c.level));
    for (const c of this.allClassifications) {
      if (c.level === maxLevel) {
        this.pushSearchValue(c);
      }
      this.searchValues.sort(this.searchValueComparisonCallback);
    }
    this.searchValuesCopy = this.searchValues;
  }

  private pushSearchValue(c: Classification) {
    this.searchValues.push({
      value: c.identifier,
      label:
        c.identifier +
        ' ' +
        (this.translocoService.getActiveLang() === 'sv' ? c.name : c.nameEng),
      name: c.name,
      nameEng: c.nameEng,
      parentId: c.parentId,
    });
  }

  // Remove Classifications
  onRemoveClassificationWorkingCopy(i) {
    this.choosenItemsWorkingCopy = this.choosenItemsWorkingCopy.filter(
      (c) => c.value !== this.choosenItemsWorkingCopy[i].value,
    );
    this.searchValues = this.searchValuesCopy.filter((val) => {
      return !this.choosenItemsWorkingCopy.includes(val);
    });
    this.setPrioForChoosenItems(this.choosenItemsWorkingCopy);
    if (this.typeId === ClassificationTypes.BO) {
      this.checkMaxSelectedClassifications(1);
    } else {
      this.checkMaxSelectedClassifications(3);
    }
    let parentId;
    if (this.typeId === ClassificationTypes.FO) {
      parentId = this.selectedHuvudgrupp;
    } else if (this.typeId === ClassificationTypes.PO) {
      parentId = this.selectedGrupp;
    }
    this.classificationItems$ = parentId
      ? this.getFinalClassificationsForParent(parentId)
      : this.getClassificationsForParent(parentId);
  }

  onRemoveClassification(i) {
    this.choosenItems = this.choosenItems.filter(
      (c) => c.value !== this.choosenItems[i].value,
    );
    this.searchValues = this.searchValuesCopy.filter((val) => {
      return !this.choosenItems.includes(val);
    });
    this.setPrioForChoosenItems(this.choosenItems);
    this.setAnswerForType();
    this.errorCheck();
    this.classificationAnswer.emit(this.parsedClassificationAnswer);
    this.formValidationService.triggerSectionInvalidCheck(this.questionId);
  }

  // Descriptions
  setDescriptionItems() {
    if (this.typeId === ClassificationTypes.BO) {
      for (const c of this.allClassifications) {
        this.descriptionItems.push({
          identifier: c.identifier,
          parentId: c.parentId,
          level: c.level,
          name: c.name,
          nameEng: c.nameEng,
          isExpanded: false,
          description: c.description,
          descriptionEng: c.descriptionEng,
          children: [],
        });
      }
    } else {
      this.descriptionItems = this.setDescriptionItemAndChildren(1);
    }
  }

  setDescriptionItemAndChildren(
    level: number,
    identifier = '',
  ): DescriptionItems[] {
    let tmpList = [];
    if (level === 1) {
      for (const c of this.firstLevelList) {
        tmpList = this.addDescriptionItem(tmpList, c);
      }
    } else {
      if (
        (this.typeId === ClassificationTypes.FO && level === 4) ||
        (this.typeId === ClassificationTypes.FO && level === 5)
      ) {
        return;
      }
      for (const c of this.allClassifications) {
        if (c.parentId === identifier) {
          tmpList = this.addDescriptionItem(tmpList, c);
        }
      }
    }
    return tmpList.sort(this.objectComparisonCallback);
  }

  // Get strings for template
  getclassificationName(descriptionItem) {
    return this.translocoService.getActiveLang() === 'sv'
      ? descriptionItem.name
      : descriptionItem.nameEng;
  }

  getclassificationLabel(Classification) {
    return this.translocoService.getActiveLang() === 'sv'
      ? Classification.name
      : Classification.nameEng;
  }

  getclassificationDescription(descriptionItem) {
    return this.translocoService.getActiveLang() === 'sv'
      ? descriptionItem.description
      : descriptionItem.descriptionEng;
  }

  // Private/Other
  trackByFn(index) {
    return index;
  }

  private addDescriptionItem(tmpList: DescriptionItems[], c: Classification) {
    tmpList.push({
      identifier: c.identifier,
      parentId: c.parentId,
      level: c.level,
      name: c.name,
      nameEng: c.nameEng,
      isExpanded: false,
      description: c.description,
      descriptionEng: c.descriptionEng,
      children: this.setDescriptionItemAndChildren(c.level + 1, c.identifier),
    });
    return tmpList;
  }

  checkMaxSelectedClassifications(num: number) {
    this.maxSelectedClassifications =
      this.choosenItemsWorkingCopy.length === num ? true : false;
  }

  errorCheck() {
    if (this.choosenItems.length === 0) {
      this.errorMessage$ =
        this.readOnlyState === ReadonlyState.Editable
          ? this.typeId === ClassificationTypes.BO
            ? this.translocoService.selectTranslate('list_btn_add_one')
            : this.translocoService.selectTranslate('repeatables_min', {
                minNumberOfRepeatables: 1,
              })
          : this.readOnlyState === ReadonlyState.Locked
            ? this.translocoService.selectTranslate('field_required')
            : of(undefined);
    } else {
      this.errorMessage$ = of(undefined);
    }
    this.cdr.markForCheck();
  }

  private objectComparisonCallback(arrayItemA, arrayItemB) {
    if (parseFloat(arrayItemA.identifier) < parseFloat(arrayItemB.identifier)) {
      return -1;
    }

    if (parseFloat(arrayItemA.identifier) > parseFloat(arrayItemB.identifier)) {
      return 1;
    }
    return 0;
  }

  private searchValueComparisonCallback(arrayItemA, arrayItemB) {
    if (parseFloat(arrayItemA.value) < parseFloat(arrayItemB.value)) {
      return -1;
    }

    if (parseFloat(arrayItemA.value) > parseFloat(arrayItemB.value)) {
      return 1;
    }
    return 0;
  }

  private objectComparisonCallbackString(arrayItemA, arrayItemB) {
    if (arrayItemA.identifier < arrayItemB.identifier) {
      return -1;
    }

    if (arrayItemA.identifier > arrayItemB.identifier) {
      return 1;
    }
    return 0;
  }

  private setPrioForChoosenItems(choosenItems) {
    for (const [i, choosen] of choosenItems.entries()) {
      choosen.prio = i + 1;
    }
  }

  private returnFilteredAsDropDownList(filtered: Classification[]) {
    return filtered.map((cl: Classification) => {
      return {
        value: cl.identifier,
        label:
          cl.identifier +
          ' ' +
          (this.translocoService.getActiveLang() === 'sv'
            ? cl.name
            : cl.nameEng),
      };
    });
  }
}
